import { useId, useMemo } from "react";
import { twJoin } from "tailwind-merge";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { buttonVariants } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Tooltip } from "@/design-system/Tooltip";
import { twResolve } from "@/helpers/tw-resolve";
import { sentimentColors } from "@/modules/analytics/components/SentimentDistributionChart";
import type { CommunityFeedSentimentTopicPoint } from "@/queries/analytics/community-feed-reporting-types";
import { useTranslation } from "@/translations";
import { RuntimeWidth } from "@/ui-adapters/RuntimeWidth";

interface Props {
  topics: CommunityFeedSentimentTopicPoint[];
  selectedTopic: string | undefined;
  onSelectedTopicChange: (topic: string | undefined) => void;
}

const topicSizeClasses = ["text-body", "text-body-emphasis", "text-heading-4"] as const;
const topicSentimentDotClasses = {
  positive: "bg-background-success",
  neutral: "bg-background-warning",
  negative: "bg-background-danger",
} as const;

/**
 * Render a controlled sentiment topic map with expandable details for the selected topic.
 * Selection is owned by the parent through `selectedTopic` and `onSelectedTopicChange`.
 */
export function SentimentTopicWordCloud({ topics, selectedTopic, onSelectedTopicChange }: Props): React.ReactNode {
  const { t } = useTranslation();
  const detailId = useId();
  const visualTopics = useMemo(() => getVisualTopics(topics), [topics]);
  const selectedVisualTopic = visualTopics.find(({ topic }) => topic.topic === selectedTopic);

  if (visualTopics.length === 0) {
    return null;
  }

  return (
    <Stack
      gap="md"
      onKeyDown={(event) => {
        if (event.key === "Escape" && selectedTopic !== undefined) {
          onSelectedTopicChange(undefined);
        }
      }}
    >
      <section className="flex flex-wrap items-center gap-x-space-sm gap-y-space-xs">
        {visualTopics.map(({ topic, sizeClass }) => {
          const sentiment = getSentiment(topic.averageScore);
          const isSelected = topic.topic === selectedTopic;
          const tooltip = `${t("page.analytics-community-feed.sentiment.analyzed-posts")}: ${topic.analyzedPosts}\n${t(
            "page.analytics-community-feed.sentiment.average-score",
          )}: ${topic.averageScore.toFixed(1)}/10`;

          return (
            <Tooltip key={topic.topic} tooltip={tooltip}>
              <button
                type="button"
                className={twResolve(
                  buttonVariants({ variant: "ghostSecondary", size: "sm" }),
                  "h-auto max-w-full gap-space-sm rounded-md border border-transparent px-space-sm py-space-xs text-left wrap-break-word whitespace-normal text-text-brand-secondary hover:bg-background-brand-light focus-visible:ring-1 focus-visible:ring-border-brand focus-visible:ring-offset-1",
                  sizeClass,
                  isSelected && "border-border-brand bg-background-brand-light",
                )}
                aria-controls={isSelected ? detailId : undefined}
                aria-expanded={isSelected}
                aria-label={`${topic.topic}. ${tooltip}`}
                aria-pressed={isSelected}
                data-sentiment={sentiment}
                onClick={() => onSelectedTopicChange(isSelected ? undefined : topic.topic)}
              >
                <span
                  className={twJoin("block size-space-sm shrink-0 rounded-full", topicSentimentDotClasses[sentiment])}
                  aria-hidden="true"
                />
                <span>{topic.topic}</span>
              </button>
            </Tooltip>
          );
        })}
      </section>

      {selectedVisualTopic && <TopicDetails id={detailId} topic={selectedVisualTopic.topic} />}

      <ChartLegend
        items={[
          { label: t("page.analytics-community-feed.sentiment.positive"), color: sentimentColors.positive },
          { label: t("page.analytics-community-feed.sentiment.neutral"), color: sentimentColors.neutral },
          { label: t("page.analytics-community-feed.sentiment.negative"), color: sentimentColors.negative },
        ]}
      />
    </Stack>
  );
}

interface TopicDetailsProps {
  id: string;
  topic: CommunityFeedSentimentTopicPoint;
}

function TopicDetails({ id, topic }: TopicDetailsProps): React.ReactNode {
  const { t } = useTranslation();
  const totalPosts =
    topic.distribution.positivePosts + topic.distribution.neutralPosts + topic.distribution.negativePosts;
  const positivePercentage = getPercentage(topic.distribution.positivePosts, totalPosts);
  const neutralPercentage = getPercentage(topic.distribution.neutralPosts, totalPosts);
  const negativePercentage = getPercentage(topic.distribution.negativePosts, totalPosts);

  return (
    <section
      id={id}
      className="flex w-full flex-col gap-space-md border-t border-border-default pt-space-md"
      role="region"
      aria-label={topic.topic}
    >
      <header className="flex flex-col gap-x-space-lg gap-y-space-xs @md:flex-row @md:items-baseline @md:justify-between">
        <h4 className="min-w-0 text-body-emphasis text-text-brand-secondary">{topic.topic}</h4>
        <div className="flex flex-wrap items-baseline gap-x-space-md gap-y-space-xs">
          <span className="flex items-baseline gap-space-sm text-caption text-text-secondary">
            <span>{t("page.analytics-community-feed.sentiment.analyzed-posts")}</span>
            <span className="text-caption-emphasis text-text-primary">{topic.analyzedPosts}</span>
          </span>
          <span className="text-body-emphasis text-text-brand-secondary">{topic.averageScore.toFixed(1)}/10</span>
        </div>
      </header>

      <div className="flex h-space-sm w-full overflow-hidden rounded-full bg-background-secondary" aria-hidden="true">
        <RuntimeWidth width={`${positivePercentage}%`}>
          {(widthProps) => <span className="bg-background-success" {...widthProps} />}
        </RuntimeWidth>
        <RuntimeWidth width={`${neutralPercentage}%`}>
          {(widthProps) => <span className="bg-background-warning" {...widthProps} />}
        </RuntimeWidth>
        <RuntimeWidth width={`${negativePercentage}%`}>
          {(widthProps) => <span className="bg-background-danger" {...widthProps} />}
        </RuntimeWidth>
      </div>

      <dl className="flex flex-wrap gap-x-space-lg gap-y-space-sm text-caption">
        <DistributionValue
          colorClass="bg-background-success"
          label={t("page.analytics-community-feed.sentiment.positive")}
          value={topic.distribution.positivePosts}
        />
        <DistributionValue
          colorClass="bg-background-warning"
          label={t("page.analytics-community-feed.sentiment.neutral")}
          value={topic.distribution.neutralPosts}
        />
        <DistributionValue
          colorClass="bg-background-danger"
          label={t("page.analytics-community-feed.sentiment.negative")}
          value={topic.distribution.negativePosts}
        />
      </dl>
    </section>
  );
}

interface DistributionValueProps {
  colorClass: string;
  label: string;
  value: number;
}

function DistributionValue({ colorClass, label, value }: DistributionValueProps): React.ReactNode {
  return (
    <Inline gap="sm" align="center" wrap={false}>
      <span className={twJoin("block size-space-xs shrink-0 rounded-full", colorClass)} aria-hidden="true" />
      <dt className="text-text-secondary">{label}</dt>
      <dd className="text-caption-emphasis text-text-primary">{value}</dd>
    </Inline>
  );
}

interface VisualTopic {
  topic: CommunityFeedSentimentTopicPoint;
  sizeClass: string;
}

function getVisualTopics(topics: CommunityFeedSentimentTopicPoint[]): VisualTopic[] {
  if (topics.length === 0) {
    return [];
  }

  const analyzedPostCounts = topics.map((topic) => topic.analyzedPosts);
  const minimumAnalyzedPosts = Math.min(...analyzedPostCounts);
  const maximumAnalyzedPosts = Math.max(...analyzedPostCounts);

  return [...topics]
    .sort((first, second) => second.analyzedPosts - first.analyzedPosts)
    .map((topic) => ({
      topic,
      sizeClass: getTopicSizeClass(topic.analyzedPosts, minimumAnalyzedPosts, maximumAnalyzedPosts),
    }));
}

function getTopicSizeClass(analyzedPosts: number, minimum: number, maximum: number): string {
  if (minimum === maximum) {
    return topicSizeClasses[1];
  }

  const relativeWeight = (analyzedPosts - minimum) / (maximum - minimum);
  const sizeIndex = Math.min(Math.floor(relativeWeight * topicSizeClasses.length), topicSizeClasses.length - 1);

  return topicSizeClasses[sizeIndex];
}

function getSentiment(averageScore: number): keyof typeof sentimentColors {
  if (averageScore >= 7) {
    return "positive";
  }

  if (averageScore >= 5) {
    return "neutral";
  }

  return "negative";
}

function getPercentage(value: number, total: number): number {
  return total === 0 ? 0 : (value / total) * 100;
}
